import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { supabase } from '@/integrations/supabase/client';
import { useMediaSession } from '@/hooks/useMediaSession';
import { useAudioOffline } from '@/hooks/useAudioOffline';
import { triggerHaptic } from '@/lib/haptics';

// Import fallback artwork
import albumArt1 from '@/assets/album-1.jpg';
import albumArt2 from '@/assets/album-2.jpg';
import albumArt3 from '@/assets/album-3.jpg';
import heroBand from '@/assets/hero-band.jpg';
import guitarPlayer from '@/assets/guitar-player.png';
import pianoMan from '@/assets/piano-man.jpg';

const fallbackArtwork = [albumArt1, albumArt2, albumArt3, heroBand, guitarPlayer, pianoMan];

export interface Track {
  id: string;
  title: string;
  description: string | null;
  audio_path: string;
  artwork_path: string | null;
  sort_order: number;
  is_featured?: boolean;
}

type RepeatMode = 'none' | 'all' | 'one';

interface MusicPlayerContextType {
  // State
  tracks: Track[];
  currentTrack: Track | null;
  currentTrackIndex: number | null;
  isPlaying: boolean;
  progress: number;
  duration: number;
  volume: number;
  shuffleEnabled: boolean;
  repeatMode: RepeatMode;
  isWaveformReady: boolean;
  isLoading: boolean;
  isOnline: boolean;
  isTrackCached: (trackId: string) => boolean;
  
  // Actions
  playTrack: (index: number) => void;
  pauseTrack: () => void;
  resumeTrack: () => void;
  playNextTrack: () => void;
  playPrevTrack: () => void;
  toggleShuffle: () => void;
  cycleRepeatMode: () => void;
  setVolume: (volume: number) => void;
  seekTo: (time: number) => void;
  setTracks: (tracks: Track[]) => void;
  reorderTracks: (fromIndex: number, toIndex: number) => void;
  
  // Refs for waveform
  waveformRef: React.RefObject<HTMLDivElement>;
  
  // Utilities
  getArtworkUrl: (artworkPath: string | null, index: number) => string;
  formatTime: (seconds: number) => string;
}

const MusicPlayerContext = createContext<MusicPlayerContextType | null>(null);

export function MusicPlayerProvider({ children }: { children: React.ReactNode }) {
  const [tracks, setTracksState] = useState<Track[]>([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(80);
  const [isWaveformReady, setIsWaveformReady] = useState(false);
  const [shuffleEnabled, setShuffleEnabled] = useState(false);
  const [repeatMode, setRepeatMode] = useState<RepeatMode>('none');
  const [shuffledOrder, setShuffledOrder] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const waveformRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const miniWaveformRef = useRef<HTMLDivElement>(null);

  const { isOnline, cacheTrack, isTrackCached, getCachedUrl } = useAudioOffline();

  const currentTrack = currentTrackIndex !== null ? tracks[currentTrackIndex] : null;

  // Media Session integration
  const { setPlaybackState, setPositionState } = useMediaSession(
    currentTrack
      ? {
          title: currentTrack.title,
          artist: 'Park Row',
          album: "Sunny's Juke Joint",
          artwork: getArtworkUrl(currentTrack.artwork_path, currentTrackIndex || 0),
          onPlay: () => resumeTrack(),
          onPause: () => pauseTrack(),
          onPreviousTrack: () => playPrevTrack(),
          onNextTrack: () => playNextTrack(),
        }
      : null
  );

  // Update media session playback state
  useEffect(() => {
    setPlaybackState(isPlaying ? 'playing' : 'paused');
  }, [isPlaying, setPlaybackState]);

  // Update media session position
  useEffect(() => {
    if (duration > 0) {
      setPositionState(duration, progress);
    }
  }, [duration, progress, setPositionState]);

  // Initialize WaveSurfer
  useEffect(() => {
    if (!waveformRef.current || wavesurferRef.current) return;

    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: 'hsl(var(--muted-foreground) / 0.4)',
      progressColor: 'hsl(var(--primary))',
      cursorColor: 'hsl(var(--primary))',
      cursorWidth: 2,
      barWidth: 3,
      barGap: 2,
      barRadius: 3,
      height: 60,
      normalize: true,
      backend: 'WebAudio',
    });

    wavesurfer.on('ready', () => {
      setDuration(wavesurfer.getDuration());
      setIsWaveformReady(true);
      setIsLoading(false);
      wavesurfer.setVolume(volume / 100);
    });

    wavesurfer.on('audioprocess', () => {
      setProgress(wavesurfer.getCurrentTime());
    });

    wavesurfer.on('seeking', () => {
      setProgress(wavesurfer.getCurrentTime());
    });

    wavesurfer.on('finish', () => {
      handleTrackEnd();
    });

    wavesurfer.on('error', (err) => {
      console.log('WaveSurfer error:', err);
      setDuration(180);
      setIsWaveformReady(true);
      setIsLoading(false);
    });

    wavesurferRef.current = wavesurfer;

    return () => {
      wavesurfer.destroy();
      wavesurferRef.current = null;
    };
  }, []);

  // Handle volume changes
  useEffect(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.setVolume(volume / 100);
    }
  }, [volume]);

  function getAudioUrl(audioPath: string) {
    const { data } = supabase.storage.from('juke-joint-audio').getPublicUrl(audioPath);
    return data.publicUrl;
  }

  function getArtworkUrl(artworkPath: string | null, index: number) {
    if (!artworkPath) {
      return fallbackArtwork[index % fallbackArtwork.length];
    }
    const { data } = supabase.storage.from('juke-joint-audio').getPublicUrl(artworkPath);
    return data.publicUrl;
  }

  const generateShuffledOrder = useCallback(() => {
    const order = tracks.map((_, i) => i);
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }
    setShuffledOrder(order);
    return order;
  }, [tracks]);

  const toggleShuffle = useCallback(() => {
    triggerHaptic('light');
    if (!shuffleEnabled) {
      generateShuffledOrder();
    }
    setShuffleEnabled((prev) => !prev);
  }, [shuffleEnabled, generateShuffledOrder]);

  const cycleRepeatMode = useCallback(() => {
    triggerHaptic('light');
    const modes: RepeatMode[] = ['none', 'all', 'one'];
    setRepeatMode((current) => {
      const currentIndex = modes.indexOf(current);
      return modes[(currentIndex + 1) % modes.length];
    });
  }, []);

  const getNextTrackIndex = useCallback(() => {
    if (currentTrackIndex === null) return 0;

    if (shuffleEnabled && shuffledOrder.length > 0) {
      const currentShuffleIndex = shuffledOrder.indexOf(currentTrackIndex);
      const nextShuffleIndex = (currentShuffleIndex + 1) % shuffledOrder.length;
      return shuffledOrder[nextShuffleIndex];
    }

    return (currentTrackIndex + 1) % tracks.length;
  }, [currentTrackIndex, shuffleEnabled, shuffledOrder, tracks.length]);

  const getPrevTrackIndex = useCallback(() => {
    if (currentTrackIndex === null) return tracks.length - 1;

    if (shuffleEnabled && shuffledOrder.length > 0) {
      const currentShuffleIndex = shuffledOrder.indexOf(currentTrackIndex);
      const prevShuffleIndex =
        currentShuffleIndex === 0 ? shuffledOrder.length - 1 : currentShuffleIndex - 1;
      return shuffledOrder[prevShuffleIndex];
    }

    return currentTrackIndex === 0 ? tracks.length - 1 : currentTrackIndex - 1;
  }, [currentTrackIndex, shuffleEnabled, shuffledOrder, tracks.length]);

  const playTrack = useCallback(
    async (index: number) => {
      triggerHaptic('medium');
      
      if (currentTrackIndex === index && isPlaying) {
        pauseTrack();
        return;
      }

      const track = tracks[index];
      if (!track || !wavesurferRef.current) return;

      setCurrentTrackIndex(index);
      setIsWaveformReady(false);
      setIsLoading(true);
      setProgress(0);
      setDuration(0);

      const audioUrl = getAudioUrl(track.audio_path);
      
      // Cache track for offline playback
      cacheTrack(track.id, audioUrl);
      
      // Get potentially cached URL for offline mode
      const playUrl = await getCachedUrl(track.id, audioUrl);

      wavesurferRef.current.load(playUrl);

      wavesurferRef.current.once('ready', () => {
        wavesurferRef.current?.play();
        setIsPlaying(true);
      });
    },
    [currentTrackIndex, isPlaying, tracks, cacheTrack, getCachedUrl]
  );

  const pauseTrack = useCallback(() => {
    triggerHaptic('light');
    if (wavesurferRef.current) {
      wavesurferRef.current.pause();
    }
    setIsPlaying(false);
  }, []);

  const resumeTrack = useCallback(() => {
    triggerHaptic('light');
    if (wavesurferRef.current) {
      wavesurferRef.current.play();
    }
    setIsPlaying(true);
  }, []);

  const handleTrackEnd = useCallback(() => {
    if (repeatMode === 'one') {
      if (wavesurferRef.current) {
        wavesurferRef.current.seekTo(0);
        wavesurferRef.current.play();
      }
    } else if (repeatMode === 'all' || currentTrackIndex !== tracks.length - 1 || shuffleEnabled) {
      playTrack(getNextTrackIndex());
    } else {
      setIsPlaying(false);
    }
  }, [repeatMode, currentTrackIndex, tracks.length, shuffleEnabled, getNextTrackIndex, playTrack]);

  const playNextTrack = useCallback(() => {
    triggerHaptic('light');
    playTrack(getNextTrackIndex());
  }, [getNextTrackIndex, playTrack]);

  const playPrevTrack = useCallback(() => {
    triggerHaptic('light');
    playTrack(getPrevTrackIndex());
  }, [getPrevTrackIndex, playTrack]);

  const setVolume = useCallback((newVolume: number) => {
    setVolumeState(newVolume);
  }, []);

  const seekTo = useCallback(
    (time: number) => {
      if (wavesurferRef.current && duration > 0) {
        wavesurferRef.current.seekTo(time / duration);
        setProgress(time);
      }
    },
    [duration]
  );

  const setTracks = useCallback((newTracks: Track[]) => {
    setTracksState(newTracks);
  }, []);

  const reorderTracks = useCallback((fromIndex: number, toIndex: number) => {
    setTracksState((prev) => {
      const result = [...prev];
      const [removed] = result.splice(fromIndex, 1);
      result.splice(toIndex, 0, removed);
      return result;
    });

    // Adjust current track index if needed
    if (currentTrackIndex !== null) {
      if (currentTrackIndex === fromIndex) {
        setCurrentTrackIndex(toIndex);
      } else if (fromIndex < currentTrackIndex && toIndex >= currentTrackIndex) {
        setCurrentTrackIndex(currentTrackIndex - 1);
      } else if (fromIndex > currentTrackIndex && toIndex <= currentTrackIndex) {
        setCurrentTrackIndex(currentTrackIndex + 1);
      }
    }
  }, [currentTrackIndex]);

  const formatTime = useCallback((seconds: number) => {
    if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const value: MusicPlayerContextType = {
    tracks,
    currentTrack,
    currentTrackIndex,
    isPlaying,
    progress,
    duration,
    volume,
    shuffleEnabled,
    repeatMode,
    isWaveformReady,
    isLoading,
    isOnline,
    isTrackCached,
    playTrack,
    pauseTrack,
    resumeTrack,
    playNextTrack,
    playPrevTrack,
    toggleShuffle,
    cycleRepeatMode,
    setVolume,
    seekTo,
    setTracks,
    reorderTracks,
    waveformRef,
    getArtworkUrl,
    formatTime,
  };

  return (
    <MusicPlayerContext.Provider value={value}>
      {children}
    </MusicPlayerContext.Provider>
  );
}

export function useMusicPlayer() {
  const context = useContext(MusicPlayerContext);
  if (!context) {
    throw new Error('useMusicPlayer must be used within a MusicPlayerProvider');
  }
  return context;
}
